import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Pagination, Table } from 'react-bootstrap';
import moment from 'moment';

const ITENS_POR_PAGINA = 20;
const MAX_PAGINAS_VISIVEIS = 5;

const formatarMoeda = valor =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatarDecimal = valor =>
  Number(valor || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

const GridSimulacao = ({
  titulos,
  somaValorAtual,
  somaValorCorrigido,
  totalTitulos,
  onEfetivar,
  efetivando,
}) => {
  const [paginaAtual, setPaginaAtual] = useState(1);
  const totalRegistros = titulos.length;
  const totalPaginas = Math.max(
    1,
    Math.ceil(totalRegistros / ITENS_POR_PAGINA)
  );

  useEffect(() => {
    setPaginaAtual(1);
  }, [titulos]);

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(totalPaginas);
  }, [paginaAtual, totalPaginas]);

  const titulosPagina = useMemo(() => {
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return titulos.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [titulos, paginaAtual]);

  const inicioLabel =
    totalRegistros === 0 ? 0 : (paginaAtual - 1) * ITENS_POR_PAGINA + 1;
  const fimLabel = Math.min(paginaAtual * ITENS_POR_PAGINA, totalRegistros);

  const handlePageChange = pagina => {
    if (pagina < 1 || pagina > totalPaginas) return;
    setPaginaAtual(pagina);
  };

  const itensPaginacao = useMemo(() => {
    const paginas = [];
    let inicio = Math.max(
      1,
      paginaAtual - Math.floor(MAX_PAGINAS_VISIVEIS / 2)
    );
    let fim = inicio + MAX_PAGINAS_VISIVEIS - 1;

    if (fim > totalPaginas) {
      fim = totalPaginas;
      inicio = Math.max(1, fim - MAX_PAGINAS_VISIVEIS + 1);
    }

    for (let i = inicio; i <= fim; i += 1) paginas.push(i);

    return {
      paginas,
      mostrarEllipseInicio: inicio > 1,
      mostrarEllipseFim: fim < totalPaginas,
    };
  }, [paginaAtual, totalPaginas]);

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Card.Title className="mb-0">
            Simulação — {totalTitulos} título(s)
          </Card.Title>
          <Button
            variant="success"
            onClick={onEfetivar}
            disabled={efetivando || totalTitulos === 0}
          >
            {efetivando ? 'Efetivando...' : 'Efetivar Correção'}
          </Button>
        </div>

        <Table striped bordered hover responsive size="sm">
          <thead>
            <tr>
              <th>Proposta</th>
              <th>Título</th>
              <th>Vencimento</th>
              <th className="text-right">Valor Atual</th>
              <th className="text-right">Cotação Anterior</th>
              <th className="text-right">Cotação Atual</th>
              <th className="text-right">Valor Corrigido</th>
            </tr>
          </thead>
          <tbody>
            {titulosPagina.map(row => {
              const codigoProposta = row.codigo_proposta;
              const codigoTitulo = row.codigo_titulo;
              const dataVencimento = row.data_vencimento;

              return (
                <tr key={`${codigoProposta}-${codigoTitulo}`}>
                  <td>{codigoProposta}</td>
                  <td>{codigoTitulo}</td>
                  <td>
                    {dataVencimento
                      ? moment(dataVencimento).format('DD/MM/YYYY')
                      : '—'}
                  </td>
                  <td className="text-right">
                    {formatarMoeda(row.valor_atual)}
                  </td>
                  <td className="text-right">
                    {formatarDecimal(row.cotacao_anterior)}
                  </td>
                  <td className="text-right">
                    {formatarDecimal(row.cotacao_atual)}
                  </td>
                  <td className="text-right">
                    {formatarMoeda(row.valor_corrigido)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-weight-bold">
              <td colSpan={3}>Total</td>
              <td className="text-right">{formatarMoeda(somaValorAtual)}</td>
              <td colSpan={2} />
              <td className="text-right">
                {formatarMoeda(somaValorCorrigido)}
              </td>
            </tr>
          </tfoot>
        </Table>

        <div className="d-flex justify-content-between align-items-center flex-wrap mt-3">
          <small className="text-muted">
            Mostrando {inicioLabel}-{fimLabel} de {totalRegistros}{' '}
            {totalRegistros === 1 ? 'registro' : 'registros'}
          </small>
          {totalPaginas > 1 && (
            <Pagination className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={paginaAtual === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(paginaAtual - 1)}
                disabled={paginaAtual === 1}
              />
              {itensPaginacao.mostrarEllipseInicio && (
                <Pagination.Ellipsis disabled />
              )}
              {itensPaginacao.paginas.map(pagina => (
                <Pagination.Item
                  key={pagina}
                  active={pagina === paginaAtual}
                  onClick={() => handlePageChange(pagina)}
                >
                  {pagina}
                </Pagination.Item>
              ))}
              {itensPaginacao.mostrarEllipseFim && (
                <Pagination.Ellipsis disabled />
              )}
              <Pagination.Next
                onClick={() => handlePageChange(paginaAtual + 1)}
                disabled={paginaAtual === totalPaginas}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPaginas)}
                disabled={paginaAtual === totalPaginas}
              />
            </Pagination>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default GridSimulacao;
